import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import MobileButtons from '../MobileButtons/MobileButtons';

import '../CommonShopFilters.less';
import CheckboxList from '../CheckboxList/CheckboxList';
import NoProductResult from '../../../../../shop/NoProductResult/NoProductResult';
import { SortRadioButtonList } from '../SortRadioButtonList/SortRadioButtonList';
import { handleOptionClick, handleToggleAction } from '../helpers';

const RenderTitleRow = ({ title, handleClose }) => (
  <div className="common-filter__sticky-row--top common-filter__border--bottom">
    <div className="common-filter__title">{title}</div>
    <i onClick={handleClose} className="zola-ui zola-ui-icon-close common-filter__icon--close" />
  </div>
);

RenderTitleRow.propTypes = {
  title: PropTypes.string,
  handleClose: PropTypes.func,
};

const CommonMobileFilter = ({
  toggleMobileFilters,
  onClearAll,
  facets,
  onMenuSelect,
  onFilterSelect,
  selectedFilters,
  setFilters,
  sortOptions,
  selectedSortValue,
  isMobile,
  productTotal,
}) => {
  const [openFilters, setOpenFilters] = useState({});
  const [showAll, setShowAll] = useState(false);
  const countResult = productTotal > 0 ? productTotal : '';

  const mobileTopTitle = 'Filter & Sort';
  const sortData = [{ key: 'SORT', label: 'Sort By', options: sortOptions }];

  const handleOptionSelect = useCallback(
    (category, option) => {
      handleOptionClick(onFilterSelect, selectedFilters, setFilters, category, option);
    },
    [onFilterSelect, selectedFilters, setFilters]
  );

  const showMoreShowLess = useCallback(
    (key) => {
      handleToggleAction(showAll, setShowAll, key);
    },
    [showAll]
  );

  const handleDropDownClick = useCallback(
    (key) => {
      handleToggleAction(openFilters, setOpenFilters, key);
    },
    [openFilters]
  );

  const handleClose = useCallback(() => {
    toggleMobileFilters();
  }, [toggleMobileFilters]);

  const handleSortClick = useCallback(
    (value) => {
      onMenuSelect(value);
    },
    [onMenuSelect]
  );

  /**
   * Updates selected filters list
   * @param {data} filterValue - name of the value to update filter with (ex: YELLOW)
   * mobile version has the sort option included as well
   * sortData - provide settings
   */

  const renderMobileFilters = (data) =>
    data.map(({ key, label, options }) => {
      const isRowExpanded = openFilters[key];
      const isOptionExpanded = showAll[key];
      const notPriceSectionBehaviour = !(key === 'PRICE_RANGE');
      const sortSection = key === 'SORT';
      if (!sortSection) {
        return (
          <CheckboxList
            key={key}
            apiKeyName={key}
            title={label}
            list={options}
            selectedFilters={selectedFilters[key]}
            handleSetFilter={handleOptionSelect}
            handleClear={onClearAll}
            handleDropDownClick={handleDropDownClick}
            isRowExpanded={isRowExpanded}
            notPriceSectionBehaviour={notPriceSectionBehaviour}
            showMoreShowLess={showMoreShowLess}
            isMobile={isMobile}
            isOptionExpanded={isOptionExpanded}
            sortSection={sortSection}
          />
        );
      }
      return (
        isMobile && (
          <SortRadioButtonList
            key={key}
            apiKeyName={key}
            title={label}
            isMobile={isMobile}
            list={options}
            sortSection={sortSection}
            selectedSortValue={selectedSortValue}
            handleClick={handleSortClick}
            handleDropDownClick={handleDropDownClick}
            isRowExpanded={isRowExpanded}
          />
        )
      );
    });
  const classNameOverlay = cx('common-filter__overlay-content', {
    'no-result': productTotal === 0,
  });

  return (
    <div className="common-filter">
      <div className="common-filter__dimmer" onClick={handleClose} />
      <div className={classNameOverlay}>
        <RenderTitleRow title={mobileTopTitle} handleClose={handleClose} />
        {productTotal === 0 ? (
          <NoProductResult onClearAll={onClearAll} />
        ) : (
          <>
            {isMobile && renderMobileFilters(sortData)}
            {renderMobileFilters(facets)}
          </>
        )}
      </div>
      <MobileButtons
        selectedFilter={selectedFilters}
        countResult={countResult}
        handleClose={handleClose}
        onClearAll={onClearAll}
      />
    </div>
  );
};

export default CommonMobileFilter;

CommonMobileFilter.propTypes = {
  toggleMobileFilters: PropTypes.func,
  isMobile: PropTypes.bool,
  onClearAll: PropTypes.func,
  onMenuSelect: PropTypes.func,
  facets: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
        })
      ),
    })
  ),
  shopEventData: PropTypes.shape({}),
  onFilterSelect: PropTypes.shape([]),
  selectedFilters: PropTypes.shape({}),
  sortOptions: PropTypes.shape({}),
  setFilters: PropTypes.func,
  productTotal: PropTypes.number,
  selectedSortValue: PropTypes.string,
};
